import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from 'react';
import { areColumnVisibilityModelsEqual } from '@/features/shared/components/tableSystem/columnVisibilityModel';
import { MountedTableTabPanel } from '@/features/shared/components/tableSystem/MountedTableTabPanel';
import { NativeTablePanelTabs } from '@/features/shared/components/tableSystem/NativeTablePanelTabs';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { useMountedTableTabs } from '@/features/shared/components/tableSystem/useMountedTableTabs';
import {
  suiteRunColumnOptions as tabbedExecutionColumnOptions,
  suiteRunDefaultColumnVisibilityModel as tabbedExecutionDefaultColumnVisibilityModel,
  suiteRunDefaultSortDirection as tabbedExecutionDefaultSortDirection,
  suiteRunDefaultSortKey as tabbedExecutionDefaultSortKey,
  suiteRunSortOptions as tabbedExecutionSortOptions,
  suiteRunStatusOptions as tabbedExecutionStatusOptions,
  type SuiteRunSortDirection,
  type SuiteRunSortKey,
} from '@/features/suites/pages/suiteRunsTableDefinition';
import {
  suiteColumnOptions as tabbedColumnOptions,
  suiteDefaultColumnVisibilityModel as tabbedDefaultColumnVisibilityModel,
  suiteDefaultSortDirection as tabbedDefaultSortDirection,
  suiteDefaultSortKey as tabbedDefaultSortKey,
  suiteQuickSortOptions as tabbedQuickSortOptions,
  suiteSortOptions as tabbedSortOptions,
  suiteStatusOptions as tabbedStatusOptions,
  type SuiteSortDirection,
  type SuiteSortKey,
} from '@/features/suites/pages/suitesTableDefinition';
import { getFilterLabel } from '@/shared/copy/filters';
import { Stack } from '@/ui';
import { Panel } from '@/ui/composites/Panel';
import { Box, type GridColumnVisibilityModel } from '@/ui/primitives';
import { SuiteRunsTable } from '../components/SuiteRunsTable';
import { SuitesTable } from '../components/SuitesTable';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteRunDto, SuiteRunStatus, SuiteListItemDto } from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

type TabbedSuitesPanelTab = 'suites' | 'executions';

const suitesPanelTabs: ReadonlyArray<{ label: string; value: TabbedSuitesPanelTab }> = [
  { label: 'Suites', value: 'suites' },
  { label: 'Runs', value: 'executions' },
];

type SuitesPageTabbedPanelProps = {
  panelActiveTab: TabbedSuitesPanelTab;
  setPanelActiveTab: Dispatch<SetStateAction<TabbedSuitesPanelTab>>;
  panelDateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  setPanelDateQuickFilter: Dispatch<SetStateAction<'7d' | '14d' | '30d' | 'latest'>>;
  panelFromDate: Dayjs | null;
  setPanelFromDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelToDate: Dayjs | null;
  setPanelToDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelSortKey: SuiteSortKey;
  setPanelSortKey: Dispatch<SetStateAction<SuiteSortKey>>;
  panelSortDirection: SuiteSortDirection;
  setPanelSortDirection: Dispatch<SetStateAction<SuiteSortDirection>>;
  panelFilterStatuses: SuiteRunStatus[];
  setPanelFilterStatuses: Dispatch<SetStateAction<SuiteRunStatus[]>>;
  panelFilterSuites: string[];
  setPanelFilterSuites: Dispatch<SetStateAction<string[]>>;
  panelFilterTags: string[];
  setPanelFilterTags: Dispatch<SetStateAction<string[]>>;
  panelExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelExecutionColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelExecutionSortKey: SuiteRunSortKey;
  setPanelExecutionSortKey: Dispatch<SetStateAction<SuiteRunSortKey>>;
  panelExecutionSortDirection: SuiteRunSortDirection;
  setPanelExecutionSortDirection: Dispatch<SetStateAction<SuiteRunSortDirection>>;
  panelAvailableTags: string[];
  suiteOptions: Array<{ value: string; label: string }>;
  panelDateWindow: {
    startDay: string | null;
    endDay: string | null;
    sparklineRangeDays: 7 | 14 | 30;
  };
  panelStatusFilterKey: string;
  panelSuiteFilterKey: string;
  panelTagFilterKey: string;
  panelNoRowsText: string;
  panelExecutionsNoRowsText: string;
  debouncedPanelSearch: string;
  panelSuitesMeta: TableQueryMeta<SuiteListItemDto> | null;
  setPanelSuitesMeta: Dispatch<SetStateAction<TableQueryMeta<SuiteListItemDto> | null>>;
  panelSuiteRunsMeta: TableQueryMeta<SuiteRunDto> | null;
  setPanelSuiteRunsMeta: Dispatch<SetStateAction<TableQueryMeta<SuiteRunDto> | null>>;
  panelSuiteRunDateBounds: { from: Dayjs | null; to: Dayjs | null };
  showPanelDeltas: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  handleDebouncedPanelSearchChange: (value: string) => void;
  panelQueryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  panelExecutionQueryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  tagOptionsOverride: TagOption[];
  disableEntityLinks?: boolean;
  disableEntitySummaryLinks?: boolean;
  disableExecutionLinks?: boolean;
  disableExecutionSummaryLinks?: boolean;
  hideExecutionDetailsColumn?: boolean;
  hideExecutionPreviewDateLabel?: boolean;
  hideDateRangeLabel?: boolean;
  disablePageSizeSelect?: boolean;
  tableRowHeight?: number;
  statusOptionsOverride?: Array<{ value: SuiteRunStatus; label: string }>;
  executionStatusOptionsOverride?: Array<{ value: SuiteRunStatus; label: string }>;
};

export const SuitesPageTabbedPanel = ({
  panelActiveTab,
  setPanelActiveTab,
  panelDateQuickFilter,
  setPanelDateQuickFilter,
  panelFromDate,
  setPanelFromDate,
  panelToDate,
  setPanelToDate,
  panelColumnVisibilityModel,
  setPanelColumnVisibilityModel,
  panelSortKey,
  setPanelSortKey: _setPanelSortKey,
  panelSortDirection,
  setPanelSortDirection: _setPanelSortDirection,
  panelFilterStatuses,
  setPanelFilterStatuses,
  panelFilterSuites,
  setPanelFilterSuites,
  panelFilterTags,
  setPanelFilterTags,
  panelExecutionColumnVisibilityModel,
  setPanelExecutionColumnVisibilityModel,
  panelExecutionSortKey,
  setPanelExecutionSortKey,
  panelExecutionSortDirection,
  setPanelExecutionSortDirection,
  panelAvailableTags,
  suiteOptions,
  panelDateWindow,
  panelStatusFilterKey,
  panelSuiteFilterKey,
  panelTagFilterKey,
  panelNoRowsText,
  panelExecutionsNoRowsText,
  debouncedPanelSearch,
  panelSuitesMeta,
  setPanelSuitesMeta,
  panelSuiteRunsMeta,
  setPanelSuiteRunsMeta,
  panelSuiteRunDateBounds,
  showPanelDeltas,
  selectedOrganisationId,
  selectedProjectId,
  handleDebouncedPanelSearchChange,
  panelQueryFn,
  panelExecutionQueryFn,
  tagOptionsOverride,
  disableEntityLinks = false,
  disableEntitySummaryLinks = false,
  disableExecutionLinks = false,
  disableExecutionSummaryLinks = false,
  hideExecutionDetailsColumn = false,
  hideExecutionPreviewDateLabel = false,
  hideDateRangeLabel = true,
  disablePageSizeSelect = false,
  tableRowHeight,
  statusOptionsOverride,
  executionStatusOptionsOverride,
}: SuitesPageTabbedPanelProps): ReactElement => {
  const mountedTabs = useMountedTableTabs(panelActiveTab);
  const [tableSortKey, setTableSortKey] = useState<SuiteSortKey>(panelSortKey);
  const [tableSortDirection, setTableSortDirection] =
    useState<SuiteSortDirection>(panelSortDirection);
  const [tableSuitesMeta, setTableSuitesMeta] = useState<TableQueryMeta<SuiteListItemDto> | null>(
    panelSuitesMeta,
  );
  const hasLocalTableSort =
    tableSortKey !== panelSortKey || tableSortDirection !== panelSortDirection;
  const resolvedTabbedColumnOptions = disableEntityLinks
    ? tabbedColumnOptions.filter((option) => option.field !== 'actions')
    : tabbedColumnOptions;
  const resolvedTabbedExecutionColumnOptions =
    disableExecutionLinks || hideExecutionDetailsColumn
      ? tabbedExecutionColumnOptions.filter((option) => option.field !== 'runId')
      : tabbedExecutionColumnOptions;
  const handleSuitesColumnVisibilityModelChange = useCallback(
    (nextModel: GridColumnVisibilityModel): void => {
      const resolvedModel = {
        ...nextModel,
        name: true,
      };
      setPanelColumnVisibilityModel((currentModel) =>
        areColumnVisibilityModelsEqual(currentModel, resolvedModel) ? currentModel : resolvedModel,
      );
    },
    [setPanelColumnVisibilityModel],
  );
  const handleSuiteRunsColumnVisibilityModelChange = useCallback(
    (nextModel: GridColumnVisibilityModel): void => {
      const resolvedModel = {
        ...nextModel,
        name: true,
      };
      setPanelExecutionColumnVisibilityModel((currentModel) =>
        areColumnVisibilityModelsEqual(currentModel, resolvedModel) ? currentModel : resolvedModel,
      );
    },
    [setPanelExecutionColumnVisibilityModel],
  );

  useEffect(() => {
    setTableSortKey(panelSortKey);
  }, [panelSortKey]);

  useEffect(() => {
    setTableSortDirection(panelSortDirection);
  }, [panelSortDirection]);

  useEffect(() => {
    setTableSuitesMeta(panelSuitesMeta);
  }, [panelSuitesMeta]);

  const handleSuitesQueryMeta = useCallback(
    (meta: TableQueryMeta<SuiteListItemDto> | null) => {
      setTableSuitesMeta(meta);
      if (!hasLocalTableSort) {
        setPanelSuitesMeta(meta);
      }
    },
    [hasLocalTableSort, setPanelSuitesMeta],
  );

  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> =>
      await panelQueryFn({
        ...input,
        sorts: [{ field: tableSortKey, direction: tableSortDirection }],
      }),
    [panelQueryFn, tableSortDirection, tableSortKey],
  );
  const suitesQueryKey = useMemo(
    () => [
      'suites-table',
      panelDateQuickFilter,
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      showPanelDeltas ? 'deltas-on' : 'deltas-off',
      tableSortKey,
      tableSortDirection,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      panelDateWindow.startDay,
      panelDateWindow.endDay,
    ],
    [
      panelDateQuickFilter,
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      selectedOrganisationId,
      selectedProjectId,
      showPanelDeltas,
      tableSortDirection,
      tableSortKey,
    ],
  );
  const suiteRunsQueryKey = useMemo(
    () => [
      'suite-runs-table',
      panelDateQuickFilter,
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      panelExecutionSortKey,
      panelExecutionSortDirection,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      debouncedPanelSearch,
      panelDateWindow.startDay,
      panelDateWindow.endDay,
    ],
    [
      debouncedPanelSearch,
      panelDateQuickFilter,
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelExecutionSortDirection,
      panelExecutionSortKey,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      sx={tablePanelSurfaceSx}
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            px: 'md',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto', position: 'relative', zIndex: 2 }}>
              <NativeTablePanelTabs
                value={panelActiveTab}
                onChange={(next) => {
                  window.requestAnimationFrame(() => {
                    setPanelActiveTab(next);
                  });
                }}
                ariaLabel="Suites panel tabs"
                tabs={suitesPanelTabs}
              />
            </Box>
            {panelActiveTab === 'suites' ? (
              <Box sx={{ minWidth: 0, flex: '0 1 auto', position: 'relative', zIndex: 1 }}>
                <TableToolbar<SuiteSortKey, SuiteRunStatus>
                  title={undefined}
                  searchAriaLabel="Search suites, tags"
                  searchValue={debouncedPanelSearch}
                  onDebouncedSearchChange={(value) => {
                    handleDebouncedPanelSearchChange(value);
                  }}
                  columnVisibilityModel={panelColumnVisibilityModel}
                  defaultColumnVisibilityModel={tabbedDefaultColumnVisibilityModel}
                  columnOptions={resolvedTabbedColumnOptions}
                  onColumnVisibilityModelChange={handleSuitesColumnVisibilityModelChange}
                  sortKey={tableSortKey}
                  sortDirection={tableSortDirection}
                  defaultSortKey={tabbedDefaultSortKey}
                  defaultSortDirection={tabbedDefaultSortDirection}
                  sortOptions={tabbedSortOptions}
                  quickSortOptions={tabbedQuickSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setTableSortKey(nextSortKey);
                    setTableSortDirection(nextSortDirection);
                  }}
                  filterStatuses={panelFilterStatuses}
                  statusOptions={statusOptionsOverride ?? tabbedStatusOptions}
                  onFilterStatusesChange={(next) => {
                    setPanelFilterStatuses(next);
                  }}
                  filterSuites={panelFilterSuites}
                  onFilterSuitesChange={setPanelFilterSuites}
                  suiteOptions={suiteOptions}
                  filterTags={panelFilterTags}
                  onFilterTagsChange={(next) => {
                    setPanelFilterTags(next);
                  }}
                  dateQuickFilter={panelDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setPanelDateQuickFilter(next);
                    setPanelFromDate(null);
                    setPanelToDate(null);
                  }}
                  fromDate={panelFromDate}
                  toDate={panelToDate}
                  onFromDateChange={(next) => {
                    setPanelFromDate(next);
                  }}
                  onToDateChange={(next) => {
                    setPanelToDate(next);
                  }}
                  availableTags={panelAvailableTags}
                  hideDateRangeLabel={hideDateRangeLabel}
                  showControlsWithoutSummary
                  summaryCount={tableSuitesMeta?.total ?? null}
                  summarySingularLabel="suite"
                  summaryPluralLabel="suites"
                />
              </Box>
            ) : null}
            {panelActiveTab === 'executions' ? (
              <Box sx={{ minWidth: 0, flex: '0 1 auto', position: 'relative', zIndex: 1 }}>
                <TableToolbar<SuiteRunSortKey, SuiteRunStatus>
                  title={undefined}
                  searchAriaLabel="Search suite runs"
                  searchValue={debouncedPanelSearch}
                  onDebouncedSearchChange={(value) => {
                    handleDebouncedPanelSearchChange(value);
                  }}
                  columnVisibilityModel={panelExecutionColumnVisibilityModel}
                  defaultColumnVisibilityModel={tabbedExecutionDefaultColumnVisibilityModel}
                  columnOptions={resolvedTabbedExecutionColumnOptions}
                  onColumnVisibilityModelChange={handleSuiteRunsColumnVisibilityModelChange}
                  sortKey={panelExecutionSortKey}
                  sortDirection={panelExecutionSortDirection}
                  defaultSortKey={tabbedExecutionDefaultSortKey}
                  defaultSortDirection={tabbedExecutionDefaultSortDirection}
                  sortOptions={tabbedExecutionSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setPanelExecutionSortKey(nextSortKey);
                    setPanelExecutionSortDirection(nextSortDirection);
                  }}
                  filterStatuses={panelFilterStatuses}
                  statusOptions={executionStatusOptionsOverride ?? tabbedExecutionStatusOptions}
                  onFilterStatusesChange={(next) => {
                    setPanelFilterStatuses(next);
                  }}
                  filterSuites={panelFilterSuites}
                  onFilterSuitesChange={setPanelFilterSuites}
                  suiteOptions={suiteOptions}
                  filterTags={panelFilterTags}
                  onFilterTagsChange={(next) => {
                    setPanelFilterTags(next);
                  }}
                  dateQuickFilter={panelDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setPanelDateQuickFilter(next);
                    setPanelFromDate(null);
                    setPanelToDate(null);
                  }}
                  fromDate={panelFromDate}
                  toDate={panelToDate}
                  onFromDateChange={(next) => {
                    setPanelFromDate(next);
                  }}
                  onToDateChange={(next) => {
                    setPanelToDate(next);
                  }}
                  availableTags={panelAvailableTags}
                  includeLatestQuickFilter
                  hideDateRangeLabel={hideDateRangeLabel}
                  showControlsWithoutSummary
                  statusFilterLabel={getFilterLabel('status')}
                  summaryCount={panelSuiteRunsMeta?.total ?? null}
                  summarySingularLabel="suite run"
                  summaryPluralLabel="suite runs"
                  previewFromDate={
                    !hideExecutionPreviewDateLabel && panelDateQuickFilter === 'latest'
                      ? panelSuiteRunDateBounds.from
                      : null
                  }
                  previewToDate={
                    !hideExecutionPreviewDateLabel && panelDateQuickFilter === 'latest'
                      ? panelSuiteRunDateBounds.to
                      : null
                  }
                />
              </Box>
            ) : null}
          </Stack>
        </Box>
      }
    >
      {mountedTabs.has('suites') ? (
        <MountedTableTabPanel active={panelActiveTab === 'suites'}>
          <SuitesTable
            persistKey={null}
            queryKey={suitesQueryKey}
            queryFn={tableQueryFn}
            active={panelActiveTab === 'suites'}
            pageSizeOptions={[5, 10, 25, 50]}
            initialPageSize={5}
            disablePageSizeSelect={disablePageSizeSelect}
            preservePreviousData
            showPaginationSkeletonWhenRowsVisible={false}
            rowHeight={tableRowHeight}
            initialSortModel={[
              {
                field: tableSortKey,
                sort: tableSortDirection,
              },
            ]}
            showDeltas={showPanelDeltas}
            noRowsText={panelNoRowsText}
            showToolbar={false}
            disableColumnSorting
            disableColumnMenu
            search={debouncedPanelSearch}
            onQueryMeta={handleSuitesQueryMeta}
            sparklineRangeDays={panelDateWindow.sparklineRangeDays}
            metricsStartDay={panelDateWindow.startDay ?? undefined}
            metricsEndDay={panelDateWindow.endDay ?? undefined}
            enableMetricsBatch={false}
            tagOptionsOverride={tagOptionsOverride}
            isAuthenticatedOverride
            disableLinks={disableEntityLinks}
            disableSummaryLinks={disableEntitySummaryLinks}
            hideActionsColumn={disableEntityLinks}
            columnVisibilityModel={panelColumnVisibilityModel}
            onColumnVisibilityModelChange={handleSuitesColumnVisibilityModelChange}
          />
        </MountedTableTabPanel>
      ) : null}
      {mountedTabs.has('executions') ? (
        <MountedTableTabPanel active={panelActiveTab === 'executions'}>
          <SuiteRunsTable
            persistKey={null}
            queryKey={suiteRunsQueryKey}
            queryFn={panelExecutionQueryFn}
            active={panelActiveTab === 'executions'}
            pageSizeOptions={[5, 10, 25, 50]}
            initialPageSize={5}
            disablePageSizeSelect={disablePageSizeSelect}
            initialSortModel={[
              {
                field: panelExecutionSortKey,
                sort: panelExecutionSortDirection,
              },
            ]}
            noRowsText={panelExecutionsNoRowsText}
            showToolbar={false}
            disableColumnSorting
            disableColumnMenu
            preservePreviousData
            showPaginationSkeletonWhenRowsVisible={false}
            rowHeight={tableRowHeight}
            isAuthenticatedOverride
            disableLinks={disableExecutionLinks}
            disableSummaryLinks={disableExecutionSummaryLinks}
            hideDetailsColumn={hideExecutionDetailsColumn}
            hideActionsColumn={disableExecutionLinks}
            search={debouncedPanelSearch}
            onQueryMeta={setPanelSuiteRunsMeta}
            tagOptionsOverride={tagOptionsOverride}
            columnVisibilityModel={panelExecutionColumnVisibilityModel}
            onColumnVisibilityModelChange={handleSuiteRunsColumnVisibilityModelChange}
          />
        </MountedTableTabPanel>
      ) : null}
    </Panel>
  );
};
